/**
 * Tipos de documento e categorias jurídicas para o módulo de instituições.
 * Organização: tipo de documento (natureza) + categoria jurídica (hierarquia normativa).
 */

/** Tipo de documento — natureza do documento (ata, edital, convenção, etc.) */
export const DOCUMENT_TYPES = [
  { value: "edital", label: "Edital" },
  { value: "balanco", label: "Balanço" },
  { value: "ata_assembleia", label: "Ata de Assembleia" },
  { value: "ata_reuniao", label: "Ata de Reunião" },
  { value: "convencao_condominio", label: "Convenção de Condomínio" },
  { value: "convencao_gestao_interna", label: "Convenção de Gestão Interna" },
  { value: "norma_interna", label: "Norma Interna" },
  { value: "padrao_servico", label: "Padrão de Serviço" },
  { value: "produzido", label: "Documento Produzido" },
  { value: "procuracao_assembleia", label: "Procuração para Assembleia" },
  { value: "carta_credenciamento", label: "Carta de Credenciamento" },
  { value: "notificacao_inadimplencia", label: "Notificação — Inadimplência" },
  { value: "notificacao_regimento", label: "Notificação — Infração ao Regimento" },
  { value: "advertencia_formal", label: "Advertência Formal" },
  { value: "notificacao_obra", label: "Notificação — Obra Irregular" },
  { value: "convocacao_ago", label: "Convocação — AGO" },
  { value: "convocacao_age", label: "Convocação — AGE" },
  { value: "circular_informativa", label: "Circular Informativa" },
  { value: "aviso_obra_area", label: "Aviso de Obra / Interdição" },
  { value: "livro_ocorrencia", label: "Termo — Livro de Ocorrências" },
  { value: "outro", label: "Outro" },
] as const;

/** Categoria jurídica — hierarquia e origem normativa (para organização e filtros) */
export const LEGAL_CATEGORIES = [
  { value: "lei_federal", label: "Lei Federal", group: "Legislação Federal", order: 1 },
  { value: "lei_complementar", label: "Lei Complementar", group: "Legislação Federal", order: 2 },
  { value: "decreto_federal", label: "Decreto Federal", group: "Legislação Federal", order: 3 },
  { value: "lei_estadual", label: "Lei Estadual", group: "Legislação Estadual", order: 4 },
  { value: "decreto_estadual", label: "Decreto Estadual", group: "Legislação Estadual", order: 5 },
  { value: "portaria", label: "Portaria", group: "Normas e Portarias", order: 6 },
  { value: "instrucao_normativa", label: "Instrução Normativa", group: "Normas e Portarias", order: 7 },
  { value: "norma_regulamentadora", label: "Norma Regulamentadora (NR)", group: "Normas e Portarias", order: 8 },
  { value: "convencao_coletiva", label: "Convenção Coletiva / CCT", group: "Trabalho e Sindicato", order: 9 },
  { value: "sentenca_normativa", label: "Sentença Normativa", group: "Trabalho e Sindicato", order: 10 },
  { value: "documento_interno", label: "Documento Interno", group: "Documentos da Instituição", order: 11 },
  { value: "outro", label: "Outro", group: "Outros", order: 12 },
] as const;

export type LegalCategoryValue = (typeof LEGAL_CATEGORIES)[number]["value"];
export type DocumentTypeValue = (typeof DOCUMENT_TYPES)[number]["value"];

const legalCategoryMap = new Map(LEGAL_CATEGORIES.map((c) => [c.value, c]));
const documentTypeMap = new Map(DOCUMENT_TYPES.map((d) => [d.value, d]));

export function getLegalCategoryLabel(value: string | null): string {
  if (!value) return "Não classificado";
  return legalCategoryMap.get(value as LegalCategoryValue)?.label ?? value;
}

export function getLegalCategoryGroup(value: string | null): string {
  if (!value) return "Outros";
  return legalCategoryMap.get(value as LegalCategoryValue)?.group ?? "Outros";
}

export function getDocumentTypeLabel(value: string): string {
  return documentTypeMap.get(value as DocumentTypeValue)?.label ?? value;
}

/** Grupos para exibição ordenada na UI (Legislação Federal primeiro, etc.) */
export const LEGAL_GROUP_ORDER = [
  "Legislação Federal",
  "Legislação Estadual",
  "Normas e Portarias",
  "Trabalho e Sindicato",
  "Documentos da Instituição",
  "Outros",
];

/** Pastas (folder) para organização dos documentos na UI.
 *  Legislação Federal: CLT, Códigos, Constituição, NRs — alimenta a IA e permite visualização. */
export const DOCUMENT_FOLDERS = [
  { value: "legislacao_federal", label: "Legislação Federal", order: 1 },
  { value: "sesi", label: "SESI - Cartilhas", order: 2 },
  { value: "convencoes", label: "Convenções Coletivas", order: 3 },
  { value: "civil_feu_rosa", label: "Cível - Feu Rosa", order: 4 },
  { value: "civil_sede_fradinhos", label: "Cível - Sede Fradinhos", order: 5 },
  { value: "legislacao", label: "Legislação (legado)", order: 6 },
  { value: "nrs", label: "NRs (legado)", order: 7 },
  { value: "outro", label: "Outros", order: 99 },
] as const;

export type DocumentFolderValue = (typeof DOCUMENT_FOLDERS)[number]["value"];

export const FOLDER_ORDER = DOCUMENT_FOLDERS.map((f) => f.value);

const folderMap = new Map(DOCUMENT_FOLDERS.map((f) => [f.value, f]));

export function getFolderLabel(value: string | null): string {
  if (!value) return "Sem pasta";
  return folderMap.get(value as DocumentFolderValue)?.label ?? value;
}
